import { Component, OnInit, signal } from '@angular/core';
import {
  AppliedFilter,
  BaseFilter,
  FilterCriteria,
} from '@shared/components/filters/Domain/filter.model';
import { Money, Payment, PaymentList } from '@modules/payments/Domain/payment.model';
import { SearchOrigin } from '@shared/components/filters/Domain/search-method.enum';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';
import { ToastService } from '@services/ui';
import { PaymentsFacade } from '@modules/payments/Application/payments.facade';
import { PaymentService } from '@modules/payments/Application/payments.service';
import { Button } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { CustomComponentsModule } from '@shared/components/custom-components.module';
import { FilterContainerComponent } from '@shared/components/filters/Components/filter-container/filter-container.component';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { TranslateModule } from '@ngx-translate/core';
import { PipeModule } from '@shared/pipes/pipe.module';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { InstallmentsDetailsComponent } from '@modules/payment-agreement/components/installments-details/installments-details.component';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { PaymentDetailsComponent } from '@modules/payments/Components/Modals/payment-details/payment-details.component';
import { CollectorModel } from '@admin/modules/collectors/model/collectors.model';
import { FilterService } from '@shared/components/filters/Application/Services/filter.service';
import { GetPaymentMethodNameService } from '@shared/services/getPaymentMethodName.service';
import { GetIconMethodPaymentService } from '@shared/services/getIconMethodPayment.service';
import { PaymentMethod } from '@shared/constants/payment-method';

@Component({
  selector: 'app-payment-list',
  standalone: true,
  imports: [
    Button,
    ChipModule,
    ConfirmPopupModule,
    CustomComponentsModule,
    FilterContainerComponent,
    NgForOf,
    ToastModule,
    TranslateModule,
    NgIf,
    PipeModule,
    TooltipModule,
    TableModule,
    SkeletonModule,
    RouterLink,
    BadgeModule,
    InstallmentsDetailsComponent,
    ModalComponent,
    PaymentDetailsComponent,
    NgClass,
  ],
  templateUrl: './payment-list.component.html',
  styleUrl: './payment-list.component.scss',
})
export class PaymentListComponent implements OnInit {
  payments = signal<Payment[]>([]);
  allPayments = signal<Payment[]>([]);
  loading = signal<boolean>(false);
  cursor = signal<string | null>(null);
  hasMore = signal(true);
  currentPage = signal(1);
  totalResults = signal<number>(0);
  totalAmount = signal<Money[]>([{ amount: 0, currency: 'USD' }]);
  showDetails = signal(false);

  selectedPayment!: Payment;
  filters: BaseFilter[] = [];
  defaultSelectedFilters: AppliedFilter[] = [];
  collectors: CollectorModel[] = [];

  protected readonly SearchOrigin = SearchOrigin;

  constructor(
    private route: ActivatedRoute,
    private filterService: FilterStorageService,
    private filterServiceUtils: FilterService,
    private paymentService: PaymentService,
    private toast: ToastService,
    private getPaymentGatewayService: GetIconMethodPaymentService,
    private getPaymentMethodNameService: GetPaymentMethodNameService,
    protected facade: PaymentsFacade,
  ) {}

  ngOnInit(): void {
    this.collectors = this.route.snapshot.data['collectors'];

    this.filters = this.facade.getFilters(this.collectors);

    const saved = this.filterService.load(this.facade.key);

    if (saved) {
      this.facade.lastCriteria.set(saved);
      this.defaultSelectedFilters = this.filterServiceUtils.convertToAppliedFilters(
        saved,
        this.filters,
      );
      this.loadPayments(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
    } else {
      this.loadPayments(SearchOrigin.RESET_OR_RELOAD, this.facade.lastCriteria());
    }
  }

  loadPayments(origin: SearchOrigin = SearchOrigin.FILTER_CHANGE, event?: FilterCriteria) {
    this.loading.set(true);

    const criteria = this.facade.resolveSearchCriteria(event, origin);

    //TODO: Extract on 3rd use
    if (origin === SearchOrigin.LOAD_NEXT) {
      // Check if we already have enough data
      const start = this.currentPage() * this.facade.numResultsDisplayed();
      const end = start + this.facade.numResultsDisplayed();

      if (end <= this.allPayments().length) {
        this.currentPage.set(this.currentPage() + 1);
        this.payments.set(this.allPayments().slice(start, end));
        this.loading.set(false);
        this.hasMore.set(this.allPayments().length > end);
        return;
      }

      criteria.pagination.cursor = this.allPayments().at(-1)?.id ?? null;
      this.currentPage.set(this.currentPage() + 1);
    } else if (origin === SearchOrigin.LOAD_PREV) {
      if (this.currentPage() > 1) this.currentPage.set(this.currentPage() - 1);

      const start = (this.currentPage() - 1) * this.facade.numResultsDisplayed();
      const end = start + this.facade.numResultsDisplayed();
      this.payments.set(this.allPayments().slice(start, end));
      this.hasMore.set(true);
      this.loading.set(false);
      return;
    } else if (origin === SearchOrigin.RESET_OR_RELOAD || origin === SearchOrigin.FILTER_CHANGE) {
      this.currentPage.set(1);
      this.allPayments.set([]);
    }

    //TODO :handle call thorough facade
    this.paymentService.getPayments(criteria).subscribe({
      next: (calls) => {
        const merged =
          origin === SearchOrigin.LOAD_NEXT
            ? [...this.allPayments(), ...calls.results]
            : calls.results;

        this.totalResults.set(calls.totalResults);
        this.totalAmount.set(calls.totalAmounts);
        this.allPayments.set(merged);

        const start = (this.currentPage() - 1) * this.facade.numResultsDisplayed();
        const end = start + this.facade.numResultsDisplayed();
        this.payments.set(merged.slice(start, end));

        this.hasMore.set(calls.results.length === this.facade.numResultsDisplayed());
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading outbound calls:', err);
        this.loading.set(false);
      },
    });
  }

  resetCalls() {
    this.currentPage.set(1);
    this.cursor.set(null);
    this.hasMore.set(true);

    this.loadPayments(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
  }

  setPageSize(size: number) {
    this.facade.setPageSize(size);
    this.currentPage.set(1);
    this.loadPayments(SearchOrigin.FILTER_CHANGE, this.facade.lastCriteria());
  }

  copyExternalId(externalId: string) {
    if (!externalId) {
      return;
    }
    window.navigator.clipboard.writeText(externalId);
    this.toast.showInfo('Copiado', `ID ${externalId} copiada al portapapeles`);
  }

  openDetails(pa: Payment) {
    this.selectedPayment = pa;
    this.showDetails.set(true);
  }

  getFriendlyMethodName(payment: Payment): string {
    return this.getPaymentMethodNameService.getFriendlyMethodPaymentName(payment.method);
  }
  getFriendlyGatewayName(payment: Payment): string {
    return this.getPaymentGatewayService.getFriendlyName(payment.paymentGateway);
  }

  protected readonly PaymentMethod = PaymentMethod;
}
